// Service to manage user plans and usage tracking
import { BASE_URL } from '../config/apiConfig';
import { isAndroid, getClientPlatform } from '../utils/environment';
import { getStoredAuthToken } from '../utils/authToken';

// Same rationale as aiService.ts's ANDROID_AI_SETTINGS_CACHE_KEY: the Android correction
// popup is a fresh WebView per open with no in-memory state carried over from the main
// window, so these let it show the last known-good plan/limit instantly instead of blocking
// on a fresh /api/auth/profile round-trip every time it opens. Desktop/iOS never touch these.
const ANDROID_USER_PLAN_CACHE_KEY = 'kalimat_android_user_plan_cache';
const ANDROID_MAX_TEXT_LENGTH_CACHE_KEY = 'kalimat_android_max_text_length_cache';

export function getCachedUserPlanForAndroid(): UserPlan | null {
  if (!isAndroid()) return null;
  try {
    const cached = localStorage.getItem(ANDROID_USER_PLAN_CACHE_KEY);
    if (!cached) return null;
    const parsed = JSON.parse(cached);
    return { ...parsed, resetDate: new Date(parsed.resetDate) };
  } catch {
    return null;
  }
}

export function getCachedMaxTextLengthForAndroid(): number | null {
  if (!isAndroid()) return null;
  const cached = localStorage.getItem(ANDROID_MAX_TEXT_LENGTH_CACHE_KEY);
  if (!cached) return null;
  const parsed = parseInt(cached, 10);
  return isNaN(parsed) ? null : parsed;
}
export interface Plan {
  name: string;
  price: number;
  maxCallsPerDay: number;
  features: string[];
}

export interface UserPlan {
  planName: string;
  usage: number;
  maxCallsPerDay: number;
  resetDate: Date;
}

// Mock data for plans
const PLANS: Record<string, Plan> = {
  free: {
    name: 'Free',
    price: 0,
    maxCallsPerDay: 25,
    features: ['Basic AI corrections']
  },
  premium: {
    name: 'Premium',
    price: 9.99,
    maxCallsPerDay: 100,
    features: ['Advanced AI corrections', 'Priority support']
  }
};

/**
 * Get user's current plan details
 */
export const getUserPlan = async (): Promise<UserPlan> => {
  // In a real implementation, this would fetch from the backend
  const planName = localStorage.getItem('user_plan') || 'free';
  const usage = parseInt(localStorage.getItem('daily_usage') || '0');
  
  return Promise.resolve({
    planName,
    usage,
    maxCallsPerDay: PLANS[planName].maxCallsPerDay,
    resetDate: new Date() // In reality, this would be the date when usage resets
  });
};

/**
 * Get user's current plan details from backend
 */
export const getUserPlanFromBackend = async (): Promise<UserPlan> => {
  try {
    const response = await fetch(`${BASE_URL}/api/auth/profile`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getStoredAuthToken()}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user plan');
    }

    const data = await response.json();
    const planName = data.user.plan || 'free';

    const result: UserPlan = {
      planName,
      usage: data.user.planDetails?.usage_count || 0,
      maxCallsPerDay: data.user.planDetails?.max_calls_per_day || PLANS[planName].maxCallsPerDay,
      resetDate: new Date()
    };

    if (isAndroid()) {
      try {
        localStorage.setItem(ANDROID_USER_PLAN_CACHE_KEY, JSON.stringify(result));
      } catch {
        // ignore cache write failures
      }
    }

    return result;
  } catch (error) {
    console.error('Error fetching user plan from backend:', error);
    // Fallback to localStorage if backend fails
    const planName = localStorage.getItem('user_plan') || 'free';
    const usage = parseInt(localStorage.getItem('daily_usage') || '0');
    
    return {
      planName,
      usage,
      maxCallsPerDay: PLANS[planName].maxCallsPerDay,
      resetDate: new Date()
    };
  }
};

/**
 * Check if user can make another API call
 */
export const canMakeCall = async (): Promise<boolean> => {
  const userPlan = await getUserPlan();
  return userPlan.usage < userPlan.maxCallsPerDay;
};

/**
 * Increment user's API call usage
 */
export const incrementUsage = async (): Promise<void> => {
  try {
    // Try to increment on backend
    const response = await fetch(`${BASE_URL}/api/auth/increment-usage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getStoredAuthToken()}`,
        'X-Client-Platform': getClientPlatform()
      }
    });

    if (response.status === 429) { // Too Many Requests
      const errorData = await response.json();
      throw new Error(errorData.error || 'Daily usage limit exceeded');
    } else if (!response.ok) {
      // Fallback to localStorage if backend fails
      const currentUsage = parseInt(localStorage.getItem('daily_usage') || '0');
      localStorage.setItem('daily_usage', (currentUsage + 1).toString());
      return;
    }

    // No need to update localStorage as the backend handles the count
  } catch (error) {
    console.error('Error incrementing usage on backend:', error);
    // Fallback to localStorage if backend fails
    const currentUsage = parseInt(localStorage.getItem('daily_usage') || '0');
    localStorage.setItem('daily_usage', (currentUsage + 1).toString());
  }
};

/**
 * Get plan details by plan name
 */
export const getPlanDetails = (planName: string): Plan | null => {
  return PLANS[planName] || null;
};

/**
 * Upgrade user's plan
 */
export const upgradePlan = async (newPlan: string): Promise<boolean> => {
  try {
    const response = await fetch(`${BASE_URL}/api/auth/upgrade-plan`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getStoredAuthToken()}`
      },
      body: JSON.stringify({ plan: newPlan })
    });
    
    if (response.ok) {
      localStorage.setItem('user_plan', newPlan);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error upgrading plan:', error);
    return false;
  }
};

/**
 * Check if user has premium plan
 */
export const hasPremiumPlan = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${BASE_URL}/api/auth/profile`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getStoredAuthToken()}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user plan');
    }

    const data = await response.json();
    return data.user.plan === 'premium';
  } catch (error) {
    console.error('Error checking premium plan:', error);
    // Fallback to localStorage if backend fails
    return localStorage.getItem('user_plan') === 'premium';
  }
};

/**
 * Get user's maximum text input length
 * Uses customizable size values from the backend (Small/Medium/Large)
 * Falls back to defaults: Small=1000, Medium=2000, Large=3000
 */
const cacheMaxTextLengthForAndroid = (value: number): number => {
  if (isAndroid()) {
    try {
      localStorage.setItem(ANDROID_MAX_TEXT_LENGTH_CACHE_KEY, String(value));
    } catch {
      // ignore cache write failures
    }
  }
  return value;
};

export const getMaxTextLength = async (): Promise<number> => {
  try {
    console.log('getMaxTextLength: Starting fetch...');
    const token = getStoredAuthToken();
    console.log('getMaxTextLength: Token available:', token ? 'yes' : 'no');
    
    const response = await fetch(`${BASE_URL}/api/auth/profile`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getStoredAuthToken()}`      }
    });

    console.log('getMaxTextLength: Response status:', response.status);

    if (!response.ok) {
      console.log('getMaxTextLength: Response not OK, throwing error');
      throw new Error('Failed to fetch user plan');
    }

    const data = await response.json();
    console.log('getMaxTextLength: Response data:', data);
    
    const features = data.user.planDetails?.features || '';
    const lines = features.split('\n').map((l: string) => l.trim());
    const planDetails = data.user.planDetails || {};
    
    console.log('Raw features:', features);
    console.log('Parsed lines:', lines);
    
    // Check which text size is selected and return the corresponding customizable value
    // Use backend values if available, otherwise fall back to defaults
    if (lines.includes('Small text input')) {
      const smallValue = planDetails.max_chars_small;
      const result = smallValue !== undefined && smallValue !== null ? smallValue : 1000;
      console.log('getMaxTextLength: Small selected, value:', result);
      return cacheMaxTextLengthForAndroid(result);
    }
    if (lines.includes('Medium text input')) {
      const mediumValue = planDetails.max_chars_medium;
      const result = mediumValue !== undefined && mediumValue !== null ? mediumValue : 2000;
      console.log('getMaxTextLength: Medium selected, value:', result);
      return cacheMaxTextLengthForAndroid(result);
    }
    if (lines.includes('Large text input')) {
      const largeValue = planDetails.max_chars_large;
      const result = largeValue !== undefined && largeValue !== null ? largeValue : 3000;
      console.log('getMaxTextLength: Large selected, value:', result);
      return cacheMaxTextLengthForAndroid(result);
    }

    // Fallback: check for old format max_text_length:
    const maxLengthLine = lines.find((line: string) => line.startsWith('max_text_length:'));
    if (maxLengthLine) {
      const value = maxLengthLine.replace('max_text_length:', '').trim();
      const parsed = parseInt(value);
      console.log('getMaxTextLength: Found old format value:', parsed);
      return cacheMaxTextLengthForAndroid(isNaN(parsed) ? 500 : parsed);
    }

    // Ultimate fallback - use small size default
    console.log('getMaxTextLength: No text size selected, using Small default (1000)');
    const fallbackValue = planDetails.max_chars_small;
    return cacheMaxTextLengthForAndroid(fallbackValue !== undefined && fallbackValue !== null ? fallbackValue : 1000);
  } catch (error) {
    console.error('getMaxTextLength: Error fetching max text length:', error);
    return 1000; // Fallback to Small default on error
  }
};
